// ─────────────────────────────────────────────────────────────────────────────
// Panneau des dimensions du query builder : hiérarchies regroupées par
// dimension (repliables), recherche, choix du niveau pour les hiérarchies
// multi-niveaux (ex. [Date].[Calendrier] : Annee › Trimestre › Mois › Jour).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import { colors, FONTS } from "../../../shared/ui/tokens";
import type { DimensionInfo, DimensionSelection } from "../types";

export type DropTarget = "rows" | "columns";

interface DimensionPanelProps {
  /** Dimensions du cube (sans « Measures »). */
  dimensions: DimensionInfo[];
  rows: DimensionSelection[];
  columns: DimensionSelection[];
  loading?: boolean;
  onAdd: (sel: DimensionSelection, target: DropTarget) => void;
  onDragStart: (sel: DimensionSelection) => void;
}

/** Niveaux réels d'une hiérarchie (sans le niveau « (All) »). */
export const realLevels = (levels: string[] | undefined): string[] =>
  (levels ?? []).filter((l) => l && l !== "(All)");

const norm = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const T = {
  text: colors.neutral[700],
  muted: colors.neutral[500],
  border: colors.neutral[200],
  item: colors.neutral[0],
  itemHover: colors.neutral[50],
  rowsBg: colors.brand[50],
  rowsBorder: colors.brand[200],
  rowsText: colors.brand[700],
  rows: colors.brand[600],
  colsBg: colors.accent[50],
  colsBorder: colors.accent[200],
  colsText: colors.accent[700],
  cols: colors.accent[600],
};

export const DimensionPanel: React.FC<DimensionPanelProps> = ({
  dimensions,
  rows,
  columns,
  loading,
  onAdd,
  onDragStart,
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  // niveau choisi pour chaque hiérarchie multi-niveaux (clé "dim|hier")
  const [levelChoice, setLevelChoice] = useState<Record<string, string>>({});

  const used = (list: DimensionSelection[], d: string, h: string) =>
    list.some((s) => s.dimensionName === d && s.hierarchyName === h);

  const q = norm(search.trim());
  const groups = useMemo(
    () =>
      dimensions
        .map((d) => ({
          dim: d,
          hierarchies: q
            ? d.hierarchies.filter((h) => norm(`${d.name} ${h.name}`).includes(q))
            : d.hierarchies,
        }))
        .filter((g) => g.hierarchies.length > 0),
    [dimensions, q],
  );

  const toggle = (name: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  if (dimensions.length === 0) {
    return (
      <div style={{ padding: 12, fontSize: 12, color: T.muted, fontStyle: "italic" }}>
        {loading ? "Chargement des dimensions…" : "Aucune dimension."}
      </div>
    );
  }

  return (
    <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 6, fontFamily: FONTS.body }}>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher une hiérarchie…"
        style={{
          width: "100%",
          padding: "7px 10px",
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          fontSize: 12,
          color: T.text,
          outline: "none",
          background: T.item,
        }}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: "calc(100vh - 210px)", overflowY: "auto" }}>
        {groups.map(({ dim, hierarchies }) => {
          const hasSelection = hierarchies.some(
            (h) => used(rows, dim.name, h.name) || used(columns, dim.name, h.name),
          );
          const isOpen = !!q || open.has(dim.name) || hasSelection;
          return (
            <div key={dim.name} style={{ border: `1px solid ${T.border}`, borderRadius: 8, background: T.item }}>
              {/* En-tête de dimension */}
              <button
                onClick={() => toggle(dim.name)}
                aria-expanded={isOpen}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 10px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: T.text,
                  textAlign: "left",
                }}
              >
                <span style={{ width: 10, color: T.muted, fontSize: 10 }}>{isOpen ? "▼" : "▶"}</span>
                <span style={{ flex: 1 }}>{dim.name}</span>
                <span
                  style={{
                    fontSize: 10.5,
                    color: T.muted,
                    background: colors.neutral[100],
                    borderRadius: 99,
                    padding: "1px 7px",
                  }}
                >
                  {hierarchies.length}
                </span>
              </button>

              {/* Hiérarchies */}
              {isOpen && (
                <div style={{ padding: "0 6px 6px", display: "flex", flexDirection: "column", gap: 3 }}>
                  {hierarchies.map((h) => {
                    const levels = realLevels(h.levels);
                    const multiLevel = levels.length > 1;
                    const key = `${dim.name}|${h.name}`;
                    // multi-niveaux : niveau le plus haut par défaut (point de départ du drill-down)
                    const levelName = levelChoice[key] ?? (multiLevel ? levels[0] : levels.at(-1) ?? h.name);
                    const sel: DimensionSelection = { dimensionName: dim.name, hierarchyName: h.name, levelName };
                    const inRows = used(rows, dim.name, h.name);
                    const inCols = used(columns, dim.name, h.name);
                    return (
                      <div
                        key={h.name}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "copy";
                          onDragStart(sel);
                        }}
                        title="Glisser vers Lignes ou Colonnes — ou cliquer L / C"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "5px 6px 5px 8px",
                          borderRadius: 6,
                          cursor: "grab",
                          background: inRows ? T.rowsBg : inCols ? T.colsBg : "transparent",
                          border: `1px solid ${inRows ? T.rowsBorder : inCols ? T.colsBorder : "transparent"}`,
                          color: inRows ? T.rowsText : inCols ? T.colsText : T.text,
                          fontSize: 12,
                        }}
                      >
                        <span style={{ fontSize: 9, color: T.muted }}>⠿</span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{
                              display: "block",
                              fontWeight: inRows || inCols ? 600 : 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h.name}
                          </span>
                          {multiLevel && (
                            <select
                              value={levelName}
                              onChange={(e) => setLevelChoice((p) => ({ ...p, [key]: e.target.value }))}
                              onClick={(e) => e.stopPropagation()}
                              title="Niveau de départ (dépliable dans le tableau)"
                              style={{
                                marginTop: 3,
                                fontSize: 11,
                                color: T.muted,
                                border: `1px solid ${T.border}`,
                                borderRadius: 5,
                                padding: "1px 4px",
                                background: T.item,
                                maxWidth: "100%",
                              }}
                            >
                              {levels.map((l) => (
                                <option key={l} value={l}>
                                  {l}
                                </option>
                              ))}
                            </select>
                          )}
                        </span>
                        {(["rows", "columns"] as const).map((target) => {
                          const active = target === "rows" ? inRows : inCols;
                          const c = target === "rows" ? T.rows : T.cols;
                          return (
                            <button
                              key={target}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAdd(sel, target);
                              }}
                              title={target === "rows" ? "Ajouter en lignes" : "Ajouter en colonnes"}
                              style={{
                                background: active ? c : "transparent",
                                color: active ? colors.neutral[0] : c,
                                border: `1px solid ${c}66`,
                                borderRadius: 4,
                                padding: "1px 6px",
                                cursor: "pointer",
                                fontSize: 10,
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {target === "rows" ? "L" : "C"}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {groups.length === 0 && (
          <div style={{ fontSize: 12, color: T.muted, padding: 6 }}>Aucune hiérarchie ne correspond.</div>
        )}
      </div>
    </div>
  );
};
